// Roadmap 2: the AI's proposal as code steps. Shows what each step does
// (never its code), whether it ran on the sample (and which of its checks
// failed), and the values to fill in, then accept or discard.
import { Button } from "antd";
import { tableLabel } from "../../../engine";
import { valueFields } from "../ai/codeShape";
import { useBuilder } from "./BuilderContext";
import ValueForm from "./ValueForm";

const StepRow = ({ step, index, result, change }) => {
  const failed = result?.status === "error";
  return (
    <li className="text-xs text-gray-700">
      <div className="flex items-start gap-1.5">
        <span className="w-4 shrink-0 text-gray-400">{index + 1}.</span>
        <span className="min-w-0 flex-1">
          <span className="font-medium">{step.label || "Code"}</span>
          <span className="block text-gray-500">{step.params.summary}</span>
          {failed && <span className="block text-red-600">{result.error}</span>}
          {!failed && result?.warnings?.map((warning) => (
            <span key={warning} className="block text-amber-700">
              {warning}
            </span>
          ))}
        </span>
        {change && <span className={`rounded px-1 text-[10px] ${change === "new" ? "bg-teal-50 text-teal-800" : "bg-amber-50 text-amber-800"}`}>{change}</span>}
      </div>
    </li>
  );
};

// onValue(stepId, name, value) stores a typed value in the draft itself, so
// it survives when the user asks the AI for more changes.
const CodeDraftCard = ({ draft, onValue, onAccept, onDiscard }) => {
  const { state } = useBuilder();
  const fields = valueFields(draft.workflow);
  // Values are optional; the failure note below only matters once they're in.
  const missing = fields.filter((f) => f.value === undefined || f.value === "" || (Array.isArray(f.value) && !f.value.length));
  const stepsWithValues = draft.workflow.steps.filter((step) => step.params?.valueFields?.length);

  const before = new Map(state.workflow.steps.map((s) => [s.id, s.params?.code]));
  const removed = state.workflow.steps.filter((s) => !draft.workflow.steps.some((p) => p.id === s.id));
  const changeOf = (step) => (!before.has(step.id) ? "new" : before.get(step.id) !== step.params.code ? "changed" : null);
  const failed = draft.run.results.some((r) => r.status === "error");

  return (
    <div className="space-y-2 rounded-xl border border-teal-200 bg-white p-3">
      <div className="text-xs font-semibold text-gray-900">Proposed steps</div>
      {draft.workflow.description && draft.workflow.description !== state.workflow.description && (
        <p className="text-xs leading-relaxed text-gray-600">{draft.workflow.description}</p>
      )}
      <ol className="space-y-1.5">
        {draft.workflow.steps.map((step, index) => (
          <StepRow key={step.id} step={step} index={index} result={draft.run.results.find((r) => r.stepId === step.id)} change={changeOf(step)} />
        ))}
        {removed.map((step) => (
          <li key={step.id} className="text-xs text-red-700 line-through">
            {step.label || step.id}
          </li>
        ))}
      </ol>
      {draft.workflow.outputs.length > 0 && (
        <div className="space-y-0.5 border-t border-gray-100 pt-2">
          <div className="text-[11px] font-semibold text-gray-700">Output files</div>
          {draft.workflow.outputs.map((output) => (
            <div key={output.id} className="text-[11px] text-gray-600">
              <span className="font-medium text-gray-800">
                {output.fileName}.{output.format}
              </span>
              {" ← "}
              {(output.sheets || []).map((sheet) => tableLabel(draft.workflow, sheet.from)).join(", ")}
            </div>
          ))}
        </div>
      )}
      {failed && !missing.length && <div className="text-[11px] text-red-600">A step still fails on your sample. You can accept and fix it, or ask the AI again.</div>}
      {fields.length > 0 && (
        <div className="space-y-2 border-t border-gray-100 pt-2">
          <div className="text-[11px] text-gray-500">Fill in the values you need; leave any that don't apply empty. They stay on your device and are never sent to the AI.</div>
          {stepsWithValues.map((step) => (
            <div key={step.id} className="space-y-1">
              {stepsWithValues.length > 1 && <div className="text-[11px] font-semibold text-gray-700">{step.label || step.id}</div>}
              <ValueForm fields={step.params.valueFields} values={step.params.values} onChange={(name, value) => onValue(step.id, name, value)} />
            </div>
          ))}
          <div className="text-[11px] text-gray-400">To change how a step works, describe it in the chat. Your answers here are kept.</div>
        </div>
      )}
      <div className="flex justify-end gap-1.5">
        <Button size="small" onClick={onDiscard}>
          Discard
        </Button>
        {/* Values used to be required: <Tooltip title="Fill in every value first"> and disabled={missing.length > 0} */}
        <Button size="small" type="primary" onClick={onAccept}>
          Accept
        </Button>
      </div>
    </div>
  );
};

export default CodeDraftCard;
